// src/composables/useInactivityLogout.js
import { onMounted, onUnmounted } from "vue";

export function useInactivityLogout(options = {}) {
  const {
    timeoutMs = 15 * 60 * 1000, // 15 min default
    onTimeout = () => {},
    storageKeyLastActive = "lastActiveAt",
    storageKeyLogoutPing = "logoutPing", // used to broadcast logout to other tabs
  } = options;

  let timerId = null;
  let bc = null; // BroadcastChannel (if supported)

  function now() {
    return Date.now();
  }

  function markActive() {
    try {
      localStorage.setItem(storageKeyLastActive, String(now()));
    } catch {}
  }

  function clearTimer() {
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  function scheduleTimer() {
    clearTimer();
    timerId = setTimeout(handleTimeout, timeoutMs);
  }

  function resetTimer() {
    markActive();
    scheduleTimer();
  }

  async function handleTimeout() {
    // double-check last activity (helps after wake from sleep)
    const last = Number(localStorage.getItem(storageKeyLastActive) || "0");
    if (last && now() - last < timeoutMs - 1000) {
      // user became active very recently; reschedule
      scheduleTimer();
      return;
    }
    await broadcastLogout();
    await onTimeout();
  }

  async function broadcastLogout() {
    // BroadcastChannel first (modern); fallback to storage event
    try {
      if (bc) bc.postMessage({ type: "logout" });
      localStorage.setItem(storageKeyLogoutPing, String(now()));
    } catch {}
  }

  function listenOtherTabs(e) {
    if (e.key === storageKeyLogoutPing) {
      // Another tab requested logout
      onTimeout();
    }
  }

  function handleVisibility() {
    // When tab becomes visible again, reset timer
    if (!document.hidden) resetTimer();
  }

  function addListeners() {
    // Activity signals
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));

    // Page visibility
    document.addEventListener("visibilitychange", handleVisibility);

    // Multi-tab via storage
    window.addEventListener("storage", listenOtherTabs);

    // BroadcastChannel (optional but nice)
    try {
      bc = new BroadcastChannel("auth");
      bc.onmessage = (msg) => {
        if (msg?.data?.type === "logout") onTimeout();
      };
    } catch {
      bc = null;
    }
  }

  function removeListeners() {
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    events.forEach((ev) => window.removeEventListener(ev, resetTimer));

    document.removeEventListener("visibilitychange", handleVisibility);
    window.removeEventListener("storage", listenOtherTabs);

    if (bc) {
      try { bc.close(); } catch {}
      bc = null;
    }
  }

  function start() {
    markActive();
    addListeners();
    scheduleTimer();
  }

  function stop() {
    removeListeners();
    clearTimer();
  }

  onMounted(start);
  onUnmounted(stop);

  return { start, stop, resetTimer, broadcastLogout };
}
